import React from 'react';
import { vatCell } from './RequisitionLineItemsTable';

/**
 * Read-only expanded row: Section 1 (header), Section 2 (Cost Control review)
 * and the line items.
 *
 * Deliberately separate from MyRequisitions' renderExpandedContent, which is
 * the same three blocks wrapped around editable inputs, decision buttons and
 * the Approve/Return/Hold/Reject bar. This one is for pages that only report
 * on a finished requisition — Approved and Rejected/Returned — where every
 * one of those controls would be wrong: the workflow is over, and there is
 * nothing left to act on.
 */

const fmt = (v) =>
  v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '0.00';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  const [y, m, day] = s.split('-');
  return y && m && day ? `${day}-${m}-${y}` : s;
};

const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };
const panel = { background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px' };
const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 };

// Blank, null and empty string all mean "not filled in" and should read the
// same. Without this a column shows nothing at all and looks broken.
const dash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

// priority_status is stored lowercase ("normal", "urgent").
const cap = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : v);

const th = { padding: '9px 10px', textAlign: 'center', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', textAlign: 'center', fontSize: 12 };
// Text reads better ranged left; figures stay centred.
const tdL = { ...td, textAlign: 'left' };

function Field({ label, value, style, span }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <div style={fieldLabel}>{label}</div>
      <div style={{ ...fieldValue, ...style }}>{value ?? '—'}</div>
    </div>
  );
}

function Decision({ accepted, rejected, held, labels }) {
  const [okLabel, noLabel, holdLabel] = labels;
  if (rejected) return <Badge text={noLabel} bg="#fee2e2" color="#dc2626" />;
  if (held) return <Badge text={holdLabel} bg="#fef3c7" color="#a16207" />;
  if (accepted) return <Badge text={okLabel} bg="#dcfce7" color="#16a34a" />;
  return <span style={{ color: '#94a3b8' }}>—</span>;
}

function Badge({ text, bg, color }) {
  return (
    <span style={{
      background: bg, color, border: `1px solid ${color}40`, padding: '3px 9px',
      borderRadius: 12, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block',
    }}>
      {text}
    </span>
  );
}

export default function RequisitionExpandedDetail({ pr }) {
  const items = pr?.items || [];

  const rejectedLines = pr?.cc_rejected_line_count != null
    ? pr.cc_rejected_line_count
    : items.filter((it) => it.cost_control_rejected).length;
  const approvedLines = items.filter((it) => it.cost_control_approved).length;
  const totalRequested = Number(pr?.total_estimated_cost || 0);
  const ccApproved = items.reduce((sum, it) => sum + Number(it.cost_control_amount ?? it.estimated_amount ?? 0), 0);
  const variance = totalRequested - ccApproved;
  const withinBudget = variance >= 0;

  // Section 2 reports what Cost Control decided. Before the request reaches
  // them there is nothing to report, and a panel of dashes reads as though
  // Cost Control looked at it and found nothing — so it is hidden until they
  // have actually reviewed. This is what a department head sees on a request
  // still sitting with them. Once CC has reviewed (including a request they
  // sent back), the figures are real and the panel returns.
  const costControlHasReviewed = !!(
    pr?.cc_reviewed_by || pr?.cc_reviewed_at || pr?.cc_review_comment
  ) || items.some((it) => (
    it.cost_control_approved || it.cost_control_rejected || it.cost_control_amount != null
  ));

  return (
    <div
      style={{
        padding: '0 0 16px 0',
        background: '#f1f5f9',
        borderTop: '2px solid #3b82f6',
        borderBottom: '2px solid #cbd5e1',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '10px 20px 8px', background: '#e8eef8', borderBottom: '1px solid #cbd5e1' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          PURPOSE: {pr?.title || '—'}
        </div>

        {/* ── Section 1 ── */}
        <div style={panel}>
          <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
            Section 1 — Requisition Header
          </div>
          <div style={grid4}>
            <Field label="Ref ID" value={pr?.pr_number || '—'} />
            <Field label="Date" value={(pr?.required_date || pr?.created_at) ? fmtDate(pr.required_date || pr.created_at) : '—'} />
            <Field label="Requester" value={pr?.requested_by_name || '—'} />
            <Field label="Employee ID" value={pr?.employee_id || '—'} />
            {/* Who the requisition is FOR, when raised on someone's behalf.
                Shown even when empty so a reviewer can tell "nobody else" from
                "nobody filled it in". */}
            <Field label="Requested For" value={pr?.requested_for || '—'} />
            <Field label="Designation" value={pr?.designation || '—'} />
            <Field label="Department" value={pr?.department_name || '—'} />
            <Field label="Request Type" value={pr?.request_type_name || '—'} />
            <Field label="Project / Site" value={pr?.project_name || '—'} />
            <Field label="Priority" value={pr?.priority_status || 'Normal'} />
            <Field label="Due Date" value={pr?.expected_due_date ? fmtDate(pr.expected_due_date) : '—'} />
            <Field label="Purpose" value={pr?.title || '—'} />
            <Field label="Remarks" value={pr?.description || pr?.remarks || '—'} />
          </div>
        </div>

        {/* ── Section 2 ── */}
        {costControlHasReviewed && (
        <div style={panel}>
          <div style={{ fontWeight: 800, color: '#2563eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
            Section 2 — Cost Control Review Summary
          </div>
          <div style={grid4}>
            <Field label="CC Officer" value={pr?.cc_reviewed_by || '—'} />
            <Field label="CC Review Date" value={pr?.cc_reviewed_at ? fmtDate(pr.cc_reviewed_at) : '—'} />
            <Field
              label="CC Comment"
              span={2}
              value={pr?.cc_review_comment ? `"${pr.cc_review_comment}"` : '—'}
              style={{ fontWeight: 600, fontStyle: pr?.cc_review_comment ? 'italic' : 'normal', lineHeight: 1.5 }}
            />
            <Field label="Approved Lines" value={approvedLines} />
            <Field label="Rejected Lines" value={rejectedLines} style={{ color: rejectedLines > 0 ? '#ef4444' : '#0f172a' }} />
            <Field label="Total Requested" value={`₦${fmt(totalRequested)}`} />
            <Field label="CC Approved" value={`₦${fmt(ccApproved)}`} style={{ color: '#2563eb' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: withinBudget ? '#16a34a' : '#ef4444' }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: withinBudget ? '#16a34a' : '#ef4444' }}>
                {withinBudget ? 'Within Budget' : 'Over Budget'}
              </span>
            </div>
            <div>
              <span style={{ ...fieldLabel, marginRight: 6 }}>Variance</span>
              <span style={{ fontWeight: 800, fontSize: 12.5, color: withinBudget ? '#16a34a' : '#ef4444' }}>₦{fmt(Math.abs(variance))}</span>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* ── Line items ── */}
      <div style={{ padding: '12px 20px 0', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid black' }}>
          <thead>
            <tr style={{ background: '#0b318bff', color: '#fff' }}>
              <th style={{ ...th, width: 34 }}>#</th>
              {/* Everything the requester filled in, in the order the creation
                  form asks for it — so a reviewer reading this table and the
                  person who typed it are looking at the same thing. */}
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>MATERIAL</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 190 }}>DESCRIPTION</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>SPEC</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>REQUESTED FOR</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>VENDOR</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>PROJECT</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>BOQ</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>SUB BOQ</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>EXPENSE CATEGORY</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 140 }}>SUB EXPENSE CATEGORY</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 100 }}>BRAND</th>
              <th style={th}>UNIT</th>
              <th style={{ ...th, background: '#134e4a' }}>REQ QTY</th>
              <th style={{ ...th, background: '#134e4a' }}>EST RATE (₦)</th>
              <th style={{ ...th, background: '#134e4a' }}>EST AMT (₦)</th>
              <th style={{ ...th, background: '#134e4a' }}>VAT (₦)</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>COST CENTRE</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 150 }}>GL ACCOUNT</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 130 }}>LEDGER</th>
              <th style={{ ...th, minWidth: 100 }}>REQ. DATE</th>
              <th style={{ ...th, minWidth: 90 }}>PRIORITY</th>
              <th style={{ ...th, textAlign: 'left', minWidth: 150 }}>REMARKS</th>
              <th style={{ ...th, background: '#0f766e' }}>CC QTY</th>
              <th style={{ ...th, background: '#0f766e' }}>CC RATE (₦)</th>
              <th style={{ ...th, background: '#0f766e' }}>CC AMT (₦)</th>
              <th style={{ ...th, background: '#0f766e' }}>CC DECISION</th>
              <th style={{ ...th, background: '#1e3a8a' }}>APPR QTY</th>
              <th style={{ ...th, background: '#1e3a8a' }}>APPR RATE (₦)</th>
              <th style={{ ...th, background: '#1e3a8a' }}>APPR AMT (₦)</th>
              <th style={{ ...th, background: '#1e3a8a' }}>APPR DECISION</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.item_id || i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={td}>{i + 1}</td>
                {/* "Others" entries live in a sibling free-text column, so each
                    of these falls through to it rather than reading blank. */}
                <td style={tdL}>{dash(item.material_name || item.material_other)}</td>
                <td style={{ ...tdL, fontWeight: 600 }}>{dash(item.description)}</td>
                <td style={tdL}>{dash(item.specification)}</td>
                <td style={tdL}>{dash(item.item_requested_for_display_name)}</td>
                <td style={tdL}>{dash(item.vendor_name || item.vendor_other)}</td>
                <td style={tdL}>{dash(item.project_name)}</td>
                <td style={tdL}>{dash(item.boq_item_description)}</td>
                <td style={tdL}>{dash(item.boq_component_description)}</td>
                <td style={tdL}>{dash(item.expense_category_name || item.expense_category_other)}</td>
                <td style={tdL}>{dash(item.expense_sub_category_name)}</td>
                <td style={tdL}>{dash(item.brand)}</td>
                {/* unit_of_measure, not unit: there is no `unit` on a line item,
                    so this column had been empty on every row. */}
                <td style={td}>{dash(item.unit_of_measure)}</td>
                <td style={{ ...td, background: '#f0fdfa' }}>{item.quantity != null ? fmt(item.quantity) : '—'}</td>
                <td style={{ ...td, background: '#f0fdfa' }}>{item.estimated_unit_rate != null ? fmt(item.estimated_unit_rate) : '—'}</td>
                <td style={{ ...td, background: '#f0fdfa', fontWeight: 700 }}>{item.estimated_amount != null ? fmt(item.estimated_amount) : '—'}</td>
                <td style={{ ...td, background: '#f0fdfa' }}>{vatCell(item)}</td>
                <td style={tdL}>{dash(item.cost_centre_name)}</td>
                <td style={tdL}>
                  {dash(item.account_ledger_code
                    ? `${item.account_ledger_code} - ${item.account_ledger_name || ''}`.replace(/ - $/, '')
                    : item.account_ledger_name)}
                </td>
                <td style={tdL}>{dash(item.ledger_name || item.ledger_other)}</td>
                <td style={td}>{item.req_date ? fmtDate(item.req_date) : '—'}</td>
                <td style={td}>{dash(cap(item.priority_status))}</td>
                <td style={tdL}>{dash(item.remarks)}</td>
                <td style={{ ...td, background: '#ecfeff' }}>{item.cost_control_quantity != null ? fmt(item.cost_control_quantity) : '—'}</td>
                <td style={{ ...td, background: '#ecfeff' }}>{item.cost_control_unit_rate != null ? fmt(item.cost_control_unit_rate) : '—'}</td>
                <td style={{ ...td, background: '#ecfeff', fontWeight: 700 }}>{item.cost_control_amount != null ? fmt(item.cost_control_amount) : '—'}</td>
                <td style={{ ...td, background: '#ecfeff' }}>
                  <Decision
                    accepted={item.cost_control_approved}
                    rejected={item.cost_control_rejected}
                    labels={['Accepted', 'Rejected', 'On Hold']}
                  />
                </td>
                <td style={{ ...td, background: '#eff6ff' }}>{item.approved_quantity != null ? fmt(item.approved_quantity) : '—'}</td>
                <td style={{ ...td, background: '#eff6ff' }}>{item.approved_unit_rate != null ? fmt(item.approved_unit_rate) : '—'}</td>
                <td style={{ ...td, background: '#eff6ff', fontWeight: 700 }}>{item.approved_amount != null ? fmt(item.approved_amount) : '—'}</td>
                <td style={{ ...td, background: '#eff6ff' }}>
                  <Decision
                    accepted={item.approved}
                    rejected={item.rejected}
                    held={item.on_hold}
                    labels={['Approved', 'Rejected', 'On Hold']}
                  />
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={30} style={{ ...td, padding: 24, color: 'var(--text-muted)' }}>No line items on this requisition.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
